import { useState, useEffect, useCallback, useRef } from "react";
import { Shield, Users, Eye, Lock } from "lucide-react";
import { toast } from "sonner";

import { SettingsToggle } from "@/features/administration/components/SettingsToggle";
import { PermissionsLoadFailed } from "@/features/administration/components/security/PermissionsLoadFailed";
import { request } from "@/lib/api";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";
import { normalizePlatformRole, PLATFORM_ROLE } from "@/utils/roles";

/* ── Permission category definitions ─────────────────────────── */

type PermissionDef = {
  key: string;
  label: string;
  adminOnly?: boolean;
  /** The roles whose value the server reads (ORG_ENFORCEMENT_MAP). Only these show a toggle. */
  enforcedFor: Array<"member" | "viewer">;
};

type PermissionCategory = {
  id: string;
  label: string;
  permissions: PermissionDef[];
};

/**
 * Display only the role×key pairs OrgPolicyService actually reads
 * (ORG_ENFORCEMENT_MAP: member.createTasks, member.deleteOwnTasks,
 * viewer.commentOnTasks). SECURITY-PAGE-TRUTH-1 (TR-2026-10-16-8 A2) removed
 * Viewer·Create tasks, Viewer·Delete own tasks and Member·Comment on tasks:
 * the server never reads them. Unwired keys stay in DEFAULT_PERMS so a save
 * does not wipe stored values.
 */
const PERMISSION_CATEGORIES: PermissionCategory[] = [
  {
    id: "tasks",
    label: "Tasks",
    permissions: [
      { key: "createTasks", label: "Create tasks", enforcedFor: ["member"] },
      { key: "deleteOwnTasks", label: "Delete own tasks", enforcedFor: ["member"] },
    ],
  },
  {
    id: "communication",
    label: "Communication",
    permissions: [
      { key: "commentOnTasks", label: "Comment on tasks", enforcedFor: ["viewer"] },
    ],
  },
];

/* ── All permission keys ─────────────────────────────────────── */

const ALL_KEYS = PERMISSION_CATEGORIES.flatMap((c) => c.permissions.map((p) => p.key));

type RolePerms = Record<string, boolean>;

/**
 * TR-2026-10-16-14: a save sends only the role×key pairs the server enforces
 * (the `enforcedFor` lists above). The server replaces each role object, so the
 * unenforced keys an org stored earlier are dropped on its next save; nothing
 * reads them, and ORG-PERMS-DTO-1 makes the server accept only these keys.
 */
function enforcedOnly(perms: Record<string, RolePerms>, role: "member" | "viewer"): RolePerms {
  const keys = PERMISSION_CATEGORIES.flatMap((c) => c.permissions)
    .filter((p) => p.enforcedFor.includes(role))
    .map((p) => p.key);
  return Object.fromEntries(keys.map((k) => [k, perms[role]?.[k] === true]));
}

const DEFAULT_PERMS: Record<string, RolePerms> = {
  admin: Object.fromEntries(ALL_KEYS.map((k) => [k, true])),
  member: {
    accessAdminConsole: false, inviteNewPeople: false, changePlatformRoles: false,
    createWorkspaces: false, deleteWorkspaces: false, manageBilling: false,
    viewAuditTrail: false, configureGovernance: false,
    createProjects: true, deleteProjects: false, assignProjectManager: false, manageProjectTeam: false,
    createTasks: true, editAnyTask: true, deleteOwnTasks: false, deleteOthersTasks: false,
    changeTaskStatus: true, assignTasks: true,
    createViews: true, createDashboards: true, customizeWorkspaceDashboard: false,
    commentOnTasks: true, uploadFiles: true, deleteFiles: false,
  },
  viewer: {
    accessAdminConsole: false, inviteNewPeople: false, changePlatformRoles: false,
    createWorkspaces: false, deleteWorkspaces: false, manageBilling: false,
    viewAuditTrail: false, configureGovernance: false,
    createProjects: false, deleteProjects: false, assignProjectManager: false, manageProjectTeam: false,
    createTasks: false, editAnyTask: false, deleteOwnTasks: false, deleteOthersTasks: false,
    changeTaskStatus: false, assignTasks: false,
    createViews: true, createDashboards: false, customizeWorkspaceDashboard: false,
    commentOnTasks: true, uploadFiles: true, deleteFiles: false,
  },
};

/* ── Role meta ───────────────────────────────────────────────── */

type OrgRole = "admin" | "member" | "viewer";

const ROLES: Array<{ id: OrgRole; label: string; icon: typeof Shield; description: string }> = [
  { id: "admin", label: "Admin", icon: Shield, description: "Full platform access (non-editable)" },
  { id: "member", label: "Member", icon: Users, description: "Standard operational access" },
  { id: "viewer", label: "Viewer", icon: Eye, description: "Read-only with limited actions" },
];

/* ── Component ───────────────────────────────────────────────── */

export function OrgPermissionsTab() {
  const [selectedRole, setSelectedRole] = useState<OrgRole>("admin");
  const [permissions, setPermissions] = useState<Record<string, RolePerms>>(DEFAULT_PERMS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const latestOkRef = useRef<Record<string, RolePerms> | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await request.get<any>("/admin/organization/permissions");
        if (!active) return;
        const stored = res?.data ?? res ?? {};
        const merged = {
          admin: DEFAULT_PERMS.admin,
          member: { ...DEFAULT_PERMS.member, ...(stored.member || {}) },
          viewer: { ...DEFAULT_PERMS.viewer, ...(stored.viewer || {}) },
        };
        setPermissions(merged);
        latestOkRef.current = merged;
      } catch {
        // SECURITY-PAGE-TRUTH-1 (A4): never fall back to defaults. They would
        // look stored, and the server replaces each role's object on PATCH, so
        // one toggle from defaults would overwrite the real values.
        if (active) setLoadFailed(true);
      } finally {
        if (active) setIsLoading(false);
      }
    })();
    return () => { active = false; };
  }, [loadAttempt]);

  const retryLoad = (): void => {
    setLoadFailed(false);
    setIsLoading(true);
    setLoadAttempt((n) => n + 1);
  };

  const persist = useCallback(async (next: Record<string, RolePerms>) => {
    setIsSaving(true);
    try {
      await request.patch("/admin/organization/permissions", {
        member: enforcedOnly(next, "member"),
        viewer: enforcedOnly(next, "viewer"),
      });
      latestOkRef.current = next;
    } catch {
      toast.error("Could not save organization permissions");
      if (latestOkRef.current) {
        setPermissions(latestOkRef.current);
      }
    } finally {
      setIsSaving(false);
    }
  }, []);

  const debouncedPersist = useDebouncedCallback((next: Record<string, RolePerms>) => {
    void persist(next);
  }, 450);

  const togglePermission = (key: string) => {
    if (normalizePlatformRole(selectedRole) === PLATFORM_ROLE.ADMIN) return;
    setPermissions((prev) => {
      const next: Record<string, RolePerms> = {
        ...prev,
        [selectedRole]: {
          ...prev[selectedRole],
          [key]: !prev[selectedRole][key],
        },
      };
      debouncedPersist(next);
      return next;
    });
  };

  const rolePerms = permissions[selectedRole] ?? {};
  // Admin bypasses every key, so it lists all of them as "Always on".
  const visibleCategories =
    selectedRole === "admin"
      ? PERMISSION_CATEGORIES
      : PERMISSION_CATEGORIES.map((c) => ({
          ...c,
          permissions: c.permissions.filter((p) => p.enforcedFor.includes(selectedRole)),
        })).filter((c) => c.permissions.length > 0);

  return (
    <div className="flex gap-6 mt-2">
      {/* Left panel — Role selector */}
      <div className="w-52 shrink-0 space-y-1">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Platform Role</p>
        {ROLES.map((role) => {
          const RIcon = role.icon;
          const active = selectedRole === role.id;
          return (
            <button
              key={role.id}
              type="button"
              onClick={() => setSelectedRole(role.id)}
              className={`w-full rounded-lg px-3 py-2.5 text-left transition-all ${
                active
                  ? "bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-sm"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              <div className="flex items-center gap-2">
                <RIcon className={`h-4 w-4 ${active ? "text-white" : "text-gray-400"}`} />
                <div>
                  <p className={`text-sm font-semibold ${active ? "text-white" : "text-gray-900"}`}>{role.label}</p>
                  <p className={`text-[11px] ${active ? "text-white/80" : "text-gray-500"}`}>{role.description}</p>
                </div>
              </div>
              {normalizePlatformRole(role.id) === PLATFORM_ROLE.ADMIN && (
                <div className="mt-1 flex items-center gap-1">
                  <Lock className={`h-3 w-3 ${active ? "text-white/70" : "text-gray-400"}`} />
                  <span className={`text-[10px] ${active ? "text-white/70" : "text-gray-400"}`}>Non-editable</span>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Right panel — Permission toggles */}
      <div className="flex-1 min-w-0">
        {isLoading ? (
          <div className="py-8 text-center text-sm text-gray-500">Loading permissions...</div>
        ) : loadFailed ? (
          <PermissionsLoadFailed onRetry={retryLoad} />
        ) : (
          <div className="space-y-6">
            {isSaving ? (
              <p className="text-xs font-medium text-neutral-500" aria-live="polite">
                Saving…
              </p>
            ) : null}
            <p className="text-xs text-neutral-600">
              Changes save automatically. Only permissions that are currently enforced are shown.
            </p>
            {visibleCategories.map((category) => (
              <div key={category.id}>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">{category.label}</h3>
                <div className="rounded-lg border border-gray-200 bg-white divide-y divide-gray-100">
                  {category.permissions.map((perm) => {
                    const isAdmin = normalizePlatformRole(selectedRole) === PLATFORM_ROLE.ADMIN;
                    const isAdminOnly =
                      perm.adminOnly && normalizePlatformRole(selectedRole) !== PLATFORM_ROLE.ADMIN;
                    const checked = isAdmin ? true : (rolePerms[perm.key] ?? false);
                    const disabled = isAdmin || isAdminOnly;

                    return (
                      <div
                        key={perm.key}
                        className={`flex items-center justify-between gap-3 px-4 py-2.5 ${
                          disabled ? "cursor-default" : "hover:bg-gray-50"
                        }`}
                      >
                        <span
                          className={`text-sm ${
                            isAdminOnly ? "text-gray-500" : "text-gray-700"
                          }`}
                        >
                          {perm.label}
                        </span>
                        <div className="flex shrink-0 items-center gap-2">
                          {isAdmin ? (
                            <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                              Always on
                            </span>
                          ) : null}
                          {isAdminOnly ? (
                            <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">
                              Admin only
                            </span>
                          ) : null}
                          <SettingsToggle
                            id={`org-perm-${perm.key}`}
                            checked={checked}
                            disabled={disabled}
                            onCheckedChange={() => togglePermission(perm.key)}
                            aria-label={perm.label}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
